import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import moment from 'moment-timezone';
import { useTheme, useMediaQuery, Typography, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { Info as InfoIcon, Undo as UndoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSelection } from '../../../components/context/SelectionContext';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface ExpenseDetail {
    id: number;
    date: string;
    createdAt?: string | Date;
    description: string;
    value: number;
    balance: number;
    boxOrigin: {
        id: number;
        balance: number;
        date: string;
        collect: {
            id: number;
            name: string;
        } | null; // collect puede ser null
    } | null; // boxOrigin puede ser null
    boxDestination: {
        id: number;
        balance: number;
        date: string;
        collect: {
            id: number;
            name: string;
        } | null; // collect puede ser null
    } | null; // boxDestination puede ser null
    expense: {
        id: number;
        name: string;
    };
    type_operation: {
        id: number;
        name: string;
    };
    collectName: string;
}

const AdminDetalleastos: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Registrar Gastos";
    const theme = useTheme();
    const { drawerOpen } = useDrawer();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { canDeleteBoxMovement } = useResourcePermissions('/administrar/cajas/detail');
    const [expensesDetail, setExpensesDetail] = useState<ExpenseDetail[]>([]);
    const { office_id, setOffice_id, collect_id, setCollect_id } = useSelection();
    const todayOperational = nowOperationalDateString();
    // Rango editable (UI)
    const [startDate, setStartDate] = useState<string>(todayOperational);
    const [endDate, setEndDate] = useState<string>(todayOperational);
    const [opFilter, setOpFilter] = useState<'all' | 'sum' | 'rest'>('all');
    const [collectFilter, setCollectFilter] = useState<'all' | string>('all');
    const [onlyPrincipal, setOnlyPrincipal] = useState<boolean>(false);

    const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);

    // Cargar oficinas y datos relacionados con office_id

    useEffect(() => {
        const fetchOffice = async () => {
            try {
                showLoading();
                setExpensesDetail([]); // Limpiar los detalles si no hay collect_id
                const { data } = await axios.get('offices');
                setOffices(data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                showAlert('Error consultando oficinas', 'error');
            } finally {
                hideLoading();
            }
        };
        fetchOffice();
    }, []); // Solo depende de office_id

    useEffect(() => {
        // Si office_id no tiene un valor válido, no ejecutar el efecto
        if (!office_id) {
            return;
        }

        // Al cambiar de oficina, resetear el filtro de cobro (lista y permisos cambian).
        setCollectFilter('all');
        setOnlyPrincipal(false);

        const fetchDataDetailOffice = async () => {
            showLoading();
            try {
                setExpensesDetail([]); // Limpiar los detalles si no hay collect_id
                const officesResponse = axios.get('offices');
                const collectsOfficeResponse = office_id ? axios.get(`collectsoffice/${office_id}`) : null;
                const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
                    params: { start_date: todayOperational, end_date: todayOperational },
                });
                setExpensesDetail(Array.isArray(data) ? data : []);

                const [officesData, collectsOfficeData] = await Promise.all([
                    officesResponse,
                    collectsOfficeResponse,
                ]);

                if (officesData) setOffices(officesData.data.data);
                if (collectsOfficeData) setCollectsOffice(collectsOfficeData.data);

                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                showAlert('Error fetching data', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchDataDetailOffice();
    }, [office_id, todayOperational]); // Solo depende de office_id

    // Si venimos desde "administrar/cajas" con un cobro seleccionado, dejarlo seteado en la UI.
    // Si es caja principal (collect_id vacío), mantener "Todos".
    useEffect(() => {
        const numeric = Number(collect_id);
        if (collect_id && Number.isFinite(numeric) && numeric > 0) {
            setOnlyPrincipal(false);
            setCollectFilter(String(numeric));
        } else {
            setCollectFilter('all');
        }
    }, [collect_id]);
    const BoxOfficeMovemente = async () => {
        showLoading();
        try {
            setExpensesDetail([]); // Limpiar los detalles si no hay collect_id
            const officesResponse = axios.get('offices');


            const [officesData] = await Promise.all([
                officesResponse,
            ]);

            if (officesData) setOffices(officesData.data.data);

            await new Promise((resolve) => setTimeout(resolve, 1000));
            showAlert('Se ha realizado la consulta exitosamente!', 'info');

        } catch (error) {
            showAlert('Error fetching data', 'error');
        } finally {
            hideLoading();
        }
    };

    const decimalFormatter = useMemo(() => new Intl.NumberFormat('en-US', { style: 'decimal' }), []);

    const formatDate = (value: string | number | undefined) => {
        if (!value) return '-';
        try {
            return format(parseISO(String(value)), 'yyyy-MM-dd HH:mm:ss');
        } catch (error) {
            return String(value);
        }
    };

    const formatDateTimeBogota = (row: SimpleRow) => {
        const raw = (row as any)?.createdAt || (row as any)?.created_at || row.date;
        if (!raw) return '-';
        const m = moment.tz(raw, 'America/Bogota');
        if (m.isValid()) {
            return m.format('YYYY-MM-DD HH:mm');
        }
        return formatDate(String(raw));
    };

    const isTodayOperational = (row: SimpleRow) => {
        const today = nowOperationalDateString(); // YYYY-MM-DD (Bogotá)
        const raw = (row as any)?.createdAt || (row as any)?.created_at || row.date;
        const m = moment.tz(raw, ['YYYY-MM-DD', moment.ISO_8601], 'America/Bogota');
        if (!m.isValid()) return false;
        return m.format('YYYY-MM-DD') === today;
    };

    const handleDeleteMovement = async (row: SimpleRow) => {
        if (!canDeleteBoxMovement) {
            return;
        }
        const id = Number(row.id);
        if (!id || !Number.isFinite(id)) {
            showAlert('No se pudo identificar el movimiento a eliminar.', 'warning');
            return;
        }
        if (!isTodayOperational(row)) {
            showAlert('Solo se pueden eliminar movimientos del día.', 'warning');
            return;
        }
        const ok = window.confirm('¿Eliminar este movimiento? Esta acción no se puede deshacer.');
        if (!ok) return;
        showLoading();
        try {
            await axios.delete(`/boxmovement/${id}`);
            showAlert('Movimiento eliminado.', 'success');
            if (office_id) {
                const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
                    params: { start_date: startDate, end_date: endDate },
                });
                setExpensesDetail(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                'No fue posible eliminar el movimiento.';
            showAlert(msg, 'error');
        } finally {
            hideLoading();
        }
    };

    const fullColumns: SimpleColumn[] = [
        {
            field: 'date',
            headerName: 'Fecha',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }: { row: SimpleRow }) => formatDateTimeBogota(row),
        },
        { field: 'expense_name', headerName: 'Detalle Gasto', flex: 1, minWidth: 180, wrapText: true },
        { field: 'description', headerName: 'Descripción', flex: 1, minWidth: 200, wrapText: true },
        { field: 'collectName', headerName: 'Cobro', flex: 1, minWidth: 160, wrapText: true },
        { field: 'type_operation_name', headerName: 'Operación', flex: 1, minWidth: 160, wrapText: true },
        {
            field: 'value',
            headerName: 'Valor',
            flex: 1,
            minWidth: 140,
            align: 'right',
            renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.value ?? 0)),
        },
        {
            field: 'options',
            headerName: 'Opciones',
            minWidth: 90,
            renderCell: ({ row }: { row: SimpleRow }) => (
                <Box display="flex" justifyContent="center" width="100%">
                    <PermissionButton
                        allowed={canDeleteBoxMovement}
                        deniedTooltip="Sin permiso para eliminar movimiento caja principal"
                        tooltip={isTodayOperational(row) ? 'Eliminar' : 'Solo se puede eliminar movimientos del día'}
                        variant="contained"
                        color="error"
                        size="small"
                        disabled={!isTodayOperational(row)}
                        onClick={() => void handleDeleteMovement(row)}
                        sx={{ minWidth: 0, padding: '2px', minHeight: 'unset', lineHeight: 1 }}
                        aria-label="Eliminar movimiento"
                    >
                        <DeleteIcon fontSize="small" />
                    </PermissionButton>
                </Box>
            ),
        },
    ];

    const mobileColumns: SimpleColumn[] = [
        { field: 'expense_name', headerName: 'Detalle Gasto', flex: 1, minWidth: 200, wrapText: true },
        { field: 'collectName', headerName: 'Cobro', flex: 1, minWidth: 150, wrapText: true },
        {
            field: 'value',
            headerName: 'Valor',
            flex: 1,
            minWidth: 140,
            align: 'right',
            renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.value ?? 0)),
        },
        {
            field: 'options',
            headerName: 'Opciones',
            minWidth: 90,
            renderCell: ({ row }: { row: SimpleRow }) => (
                <Box display="flex" justifyContent="center" width="100%">
                    <PermissionButton
                        allowed={canDeleteBoxMovement}
                        deniedTooltip="Sin permiso para eliminar movimiento caja principal"
                        tooltip={isTodayOperational(row) ? 'Eliminar' : 'Solo se puede eliminar movimientos del día'}
                        variant="contained"
                        color="error"
                        size="small"
                        disabled={!isTodayOperational(row)}
                        onClick={() => void handleDeleteMovement(row)}
                        sx={{ minWidth: 0, padding: '2px', minHeight: 'unset', lineHeight: 1 }}
                        aria-label="Eliminar movimiento"
                    >
                        <DeleteIcon fontSize="small" />
                    </PermissionButton>
                </Box>
            ),
        },
    ];

    const simpleColumns = isMobile ? mobileColumns : fullColumns;

    const rows: SimpleRow[] = expensesDetail.map((expenseDetail) => ({
        id: expenseDetail.id as RowId,
        expense_name: expenseDetail.expense.name,
        type_operation_id: expenseDetail.type_operation?.id,
        type_operation_name: expenseDetail.type_operation.name,
        collect_id:
            expenseDetail.boxOrigin?.collect?.id ??
            expenseDetail.boxDestination?.collect?.id ??
            null,
        collectName: expenseDetail.boxOrigin?.collect?.name || expenseDetail.boxDestination?.collect?.name || expenseDetail.collectName || '-',
        value: expenseDetail.value ?? 0,
        description: expenseDetail.description,
        date: expenseDetail.date,
        createdAt: (expenseDetail as any)?.createdAt || (expenseDetail as any)?.created_at || undefined,
    }));

    const filteredRows = useMemo(() => {
        let next = rows;

        if (opFilter !== 'all') {
            const wantTypeOpId = opFilter === 'sum' ? 1 : 2;
            next = next.filter((row) => Number((row as any)?.type_operation_id) === wantTypeOpId);
        }

        if (onlyPrincipal) {
            next = next.filter((row) => (row as any)?.collect_id == null);
        } else if (collectFilter !== 'all') {
            const wantCollectId = Number(collectFilter);
            next = next.filter((row) => Number((row as any)?.collect_id) === wantCollectId);
        }

        return next;
    }, [rows, opFilter, collectFilter, onlyPrincipal]);

    const fetchMovementsForRange = async (rangeStart: string, rangeEnd: string) => {
        if (!office_id) {
            showAlert('Seleccione una oficina.', 'warning');
            return;
        }
        showLoading();
        try {
            const { data } = await axios.get(`expensesdetailoffice/${office_id}`, {
                params: { start_date: rangeStart, end_date: rangeEnd },
            });
            const list = Array.isArray(data) ? data : [];
            setExpensesDetail(list);
            if (list.length === 0) {
                showAlert('No existen registros para el rango seleccionado.', 'info');
            }
        } catch (error) {
            console.error(error);
            const msg =
                (error as any)?.response?.data?.message ||
                'No fue posible consultar los movimientos.';
            showAlert(msg, 'error');
            setExpensesDetail([]);
        } finally {
            hideLoading();
        }
    };

    const handleApplyDateRange = async () => {
        if (!startDate || !endDate) {
            showAlert('Seleccione fecha inicio y fecha fin.', 'warning');
            return;
        }
        const startM = moment.tz(startDate, ['YYYY-MM-DD'], 'America/Bogota');
        const endM = moment.tz(endDate, ['YYYY-MM-DD'], 'America/Bogota');
        if (!startM.isValid() || !endM.isValid()) {
            showAlert('Rango de fechas inválido.', 'warning');
            return;
        }
        if (endM.isBefore(startM, 'day')) {
            showAlert('La fecha fin no puede ser menor que la fecha inicio.', 'warning');
            return;
        }
        await fetchMovementsForRange(startDate, endDate);
    };

    return (
        <HeaderResponsive >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Title>Detalle Movimientos Caja Oficina</Title>
                <Button
                    variant="contained"
                    startIcon={isMobile ? null : <UndoIcon />}
                    component={NavLink}
                    to="/administrar/cajas"
                >
                    {isMobile ? <UndoIcon /> : 'Regresar'}
                </Button>
            </Box>
            <Card>
                <form onSubmit={(e) => e.preventDefault()}>
                    <CardContent>
                         <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="form-layouts-separator-select-label" >
                                        Seleccionar Oficina
                                    </InputLabel>
                                    {offices && offices.length > 0 ? (
                                        <Select
                                            label="Seleccionar Oficina"
                                            value={office_id || ""} // Usa un valor por defecto (cadena vacía) si office_id es null o undefined
                                            labelId="office_id"
                                            id="office_id"
                                            onChange={(event) => {
                                                // Al cambiar oficina, limpiamos cobro seleccionado para evitar arrastrar uno de otra oficina.
                                                setOffice_id(event.target.value);
                                                setCollect_id('');
                                                setCollectFilter('all');
                                                setOnlyPrincipal(false);
                                            }}
                                            required
                                        >
                                            {offices.map((office: Office) => (
                                                <MenuItem key={office.id} value={office.id}> {/* Asegúrate de que office.id sea del mismo tipo que office_id */}
                                                    {office.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : null}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="collect-filter-label">Cobro</InputLabel>
                                    <Select
                                        labelId="collect-filter-label"
                                        label="Cobro"
                                        value={collectFilter}
                                        onChange={(e) => setCollectFilter(String(e.target.value) as any)}
                                        disabled={onlyPrincipal}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        {collectsOffice.map((collect: Collect) => (
                                            <MenuItem key={collect.id} value={String(collect.id)}>
                                                {collect.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="date"
                                    label="Fecha inicio"
                                    InputLabelProps={{ shrink: true }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="date"
                                    label="Fecha fin"
                                    InputLabelProps={{ shrink: true }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="type-operation-filter-label">Tipo</InputLabel>
                                    <Select
                                        labelId="type-operation-filter-label"
                                        label="Tipo"
                                        value={opFilter}
                                        onChange={(e) => setOpFilter(e.target.value as any)}
                                    >
                                        <MenuItem value="all">Todos</MenuItem>
                                        <MenuItem value="sum">Suma</MenuItem>
                                        <MenuItem value="rest">Resta</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={onlyPrincipal}
                                            onChange={(e) => setOnlyPrincipal(e.target.checked)}
                                        />
                                    }
                                    label="Caja Principal"
                                />
                            </Grid>                            
                            <Grid item xs={12}>
                                <Box display="flex" gap={1} justifyContent="flex-end">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={handleApplyDateRange}
                                    >
                                        Buscar
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>
                <CardContent
                    sx={{
                        width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', // o usa valores reales de tu drawer
                    }}
                >
                    {filteredRows.length > 0 ? (
                        <SimpleCustomTable
                            rows={filteredRows}
                            columns={simpleColumns}
                            maxHeight={600}
                            showBorders
                            showCount
                            exportCsv
                            exportFileName="detalle-caja.csv"
                            wrapCellContent
                        />
                    ) : (
                        <Box p={3} textAlign="center">
                            <Box display="flex" alignItems="center" justifyContent="center">
                                <InfoIcon color="info" style={{ marginRight: 8 }} /> {/* Ícono de información */}
                                <Typography variant="h6">No existen registros</Typography>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </HeaderResponsive>
    );
};

export default AdminDetalleastos;
